import React, { useEffect, useRef } from "react";
import { Brain, ChevronDown, ChevronRight, Loader2, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";

export type AgentOperation = {
  id: string;
  op: string;
  title: string;
  detail?: string;
  status: "running" | "done" | "failed";
};

export type AgentActivityPanelProps = {
  thinkingLines: string[];
  operations: AgentOperation[];
  currentStageLabel: string;
  language?: "en" | "am";
  isActive?: boolean;
};

export const AgentActivityPanel: React.FC<AgentActivityPanelProps> = ({
  thinkingLines,
  operations,
  currentStageLabel,
  language = "en",
  isActive = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [expandedOps, setExpandedOps] = React.useState<Record<string, boolean>>({});

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [thinkingLines, operations, currentStageLabel]);

  const toggleOp = (id: string) => {
    setExpandedOps((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const hasContent = thinkingLines.length > 0 || operations.length > 0;

  return (
    <div className="mx-auto w-full max-w-2xl rounded-xl border border-cyan-200/70 bg-white/95 shadow-lg shadow-cyan-100/30 backdrop-blur-sm">
      <div className="flex items-center gap-2 border-b border-cyan-100/80 px-3 py-2">
        {isActive ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-cyan-600" />
        ) : (
          <Brain className="h-3.5 w-3.5 shrink-0 text-cyan-700" />
        )}
        <span className="text-xs font-semibold text-slate-900">
          {language === "am" ? "የኤጀንት እንቅስቃሴ" : "Agent activity"}
        </span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold text-cyan-700">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-500 animate-pulse" />
          {language === "am" ? "ቀጥታ" : "LIVE"}
        </span>
      </div>

      <div ref={scrollRef} className="max-h-72 space-y-3 overflow-y-auto px-3 py-2.5 text-xs">
        <p className="text-[11px] font-medium text-slate-600">{currentStageLabel}</p>

        {thinkingLines.length > 0 ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              <Brain className="h-3 w-3" />
              {language === "am" ? "አስተሳሰብ" : "Thinking"}
            </div>
            {thinkingLines.map((line, idx) => (
              <p
                key={`think-${idx}`}
                className={cn(
                  "rounded-md border border-slate-100 bg-slate-50/90 px-2.5 py-1.5 leading-relaxed text-slate-700",
                  idx === thinkingLines.length - 1 && isActive && "border-cyan-100 bg-cyan-50/60",
                )}
              >
                {line}
              </p>
            ))}
          </div>
        ) : null}

        {operations.length > 0 ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              <Wrench className="h-3 w-3" />
              {language === "am" ? "ክወናዎች" : "Operations"}
            </div>
            {operations.map((op) => {
              const expanded = expandedOps[op.id] ?? Boolean(op.detail);
              const hasDetail = Boolean(op.detail?.trim());
              return (
                <div
                  key={op.id}
                  className="rounded-md border border-slate-200 bg-white overflow-hidden"
                >
                  <button
                    type="button"
                    className="flex w-full items-start gap-2 px-2.5 py-2 text-left hover:bg-slate-50"
                    onClick={() => hasDetail && toggleOp(op.id)}
                    disabled={!hasDetail}
                  >
                    {hasDetail ? (
                      expanded ? (
                        <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                      ) : (
                        <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                      )
                    ) : (
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-semibold text-slate-800">{op.title}</span>
                        <span
                          className={cn(
                            "rounded px-1.5 py-0.5 text-[10px] font-medium",
                            op.status === "running" && "bg-amber-50 text-amber-700",
                            op.status === "done" && "bg-emerald-50 text-emerald-700",
                            op.status === "failed" && "bg-red-50 text-red-700",
                          )}
                        >
                          {op.status}
                        </span>
                      </div>
                      {hasDetail && expanded ? (
                        <pre className="mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded border border-slate-100 bg-slate-50 p-2 font-mono text-[10px] text-slate-600">
                          {op.detail}
                        </pre>
                      ) : null}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        {!hasContent && isActive ? (
          <p className="text-[11px] text-slate-500">
            {language === "am" ? "በሂደት ላይ..." : "Starting up..."}
          </p>
        ) : null}
      </div>
    </div>
  );
};
